import { Component, computed, inject, input } from '@angular/core';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import {
  AccountType,
  CallQueue,
  MicrosoftTeamsCallQueuesFieldConfig,
  MicrosoftTeamsUserCallQueuesMembership,
} from '../../../../../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../../../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig } from '../../../../../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../../../../../forms/ziro-form-state.service';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../../../../forms/fields/select/ziro-select.component';
import { TeamsCallQueueMembershipResource } from '../../../../../../../shared/resources/teams-call-queue-membership.resource';
import { map } from 'rxjs/operators';
import { isEqual } from 'lodash';
import { ZiroIcons } from '../../../../../../../shared/models/ziro-icons.enum';
import { TranslateService } from '@ngx-translate/core';
import { Microsoft360ViewContext } from '../../../../../../../shared/contexts/microsoft-360-view.context';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonStyles } from '../../../../../../../button/button.component';

interface CallQueueFormData {
  callQueues: ZiroSelectOption[];
}

@Component({
  selector: 'ziro-teams-call-queues-form',
  templateUrl: 'teams-call-queues-form.component.html',
  styleUrls: ['teams-call-queues-form.component.scss'],
  standalone: false,
})
export class TeamsCallQueuesFormComponent extends ZiroFormAbstractDirective<CallQueueFormData> {
  protected _ziroFormStateService: ZiroFormStateService;
  private _teamsCallQueueResource = inject(TeamsCallQueueMembershipResource);
  private translateService = inject(TranslateService);
  private _microsoft360ViewContext = inject(Microsoft360ViewContext);

  readonly upn = input<string>();
  readonly tenantId = input<string>();

  private _ms360View = toSignal(this._microsoft360ViewContext.state$, { initialValue: null });

  readonly isResourceAccount = computed(() => this._ms360View()?.accountType === AccountType.RESOURCE_ACCOUNT);

  ziroIcons = ZiroIcons;

  formConfig: ZiroFormConfig = {
    fields: {
      callQueues: {
        label: 'tkey;microsoft_360.view.teams.call_queues.call_queue_membership.label',
        dataAutomation: 'call-queues',
        required: false,
        hidden: () => this.isResourceAccount(),
        valExcluded: () => this.isResourceAccount(),
        componentConfig: new ZiroSelectConfig({
          placeholder: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_queues.call_queue_membership.placeholder',
          ),
          options: [],
          isMultiSelect: true,
        }),
      },
    },
  };
  isDataInitialized = false;
  isFetchingQueueData = false;

  private _initialOptions: ZiroSelectOption[] = [];
  private subs = new Subscription();

  saveQueues(): Observable<void> {
    if (!isEqual(this._initialOptions, this.entity.callQueues)) {
      const payload: MicrosoftTeamsUserCallQueuesMembership = {
        callQueueJsons: this.entity.callQueues.map((option: ZiroSelectOption) => {
          return {
            callQueueName: option.label,
            callQueueUuid: option.value,
          };
        }),
        userPrincipalName: this.upn(),
      };

      return this._teamsCallQueueResource.saveUserQueueMemberships(this.upn(), payload, this.tenantId()).pipe(
        map((): null => {
          return null;
        }),
      );
    }

    return of(null);
  }

  getCallQueues(): void {
    this.isFetchingQueueData = true;
    const callQueueSub = forkJoin([
      this._teamsCallQueueResource.getQueueMemberships(this.tenantId()),
      this._teamsCallQueueResource.getUserQueueMemberships(this.upn(), this.tenantId()),
    ]).subscribe(([fieldConfig, userCallQueues]) => {
      this._updateEntity(userCallQueues);
      this._updateFieldOptions(fieldConfig);
      this.isFetchingQueueData = false;
      this.isDataInitialized = true;
    });

    this.subs.add(callQueueSub);
  }

  protected submit(): Observable<void> {
    return of(null);
  }

  private _updateEntity(userCallQueues: MicrosoftTeamsUserCallQueuesMembership): void {
    const options: ZiroSelectOption[] = userCallQueues.callQueueJsons.map((option: CallQueue) => {
      return {
        label: option.callQueueName,
        value: option.callQueueUuid,
      };
    });

    this._initialOptions = [...options];

    this.entitySource.next({
      ...this.entity,
      callQueues: options,
    });
  }

  private _updateFieldOptions(fieldConfig: MicrosoftTeamsCallQueuesFieldConfig): void {
    const options = fieldConfig.callQueues.possibleOptions.map((option: CallQueue) => {
      return {
        label: option.callQueueName,
        value: option.callQueueUuid,
      };
    });

    const fieldNameComponent = this.fieldComponents.find((field) => field.fieldId() === 'callQueues');
    fieldNameComponent.applyComponentConfig(
      new ZiroSelectConfig({
        placeholder: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_queues.call_queue_membership.placeholder',
        ),
        options: options,
        isMultiSelect: true,
      }),
    );
  }

  protected readonly ButtonStyles = ButtonStyles;
}
